import React, { useState, useRef } from 'react';
import { Modal, Input, Row, Col, message } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import WebIcon from 'src/_shared/components/WebIconComponent.jsx';
import { COLORS } from 'src/_shared/styles/colors';
import 'src/customizable-ui/aside/CustomizableAside.scss';

export const IconSelector = ({
	visible,
	onSelect,
	onCancel,
	availableIcons,
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [customIcons, setCustomIcons] = useState([]);
	const fileInputRef = useRef(null);

	const filteredIcons = availableIcons.filter((icon) =>
		icon.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const filteredCustomIcons = customIcons.filter((icon) =>
		icon.name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleSelect = (iconName) => {
		onSelect(iconName);
		setSearchTerm('');
	};

	const handleSelectCustomIcon = (customIcon) => {
		onSelect(customIcon);
		setSearchTerm('');
	};

	const handleCancel = () => {
		setSearchTerm('');
		onCancel();
	};

	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		const validTypes = ['image/svg+xml', 'image/png'];
		if (!validTypes.includes(file.type)) {
			message.error('Please upload only SVG or PNG files');
			return;
		}

		const maxSize = 1024 * 1024; // 1MB
		if (file.size > maxSize) {
			message.error('File size must be less than 1MB');
			return;
		}

		const reader = new FileReader();
		reader.onload = (e) => {
			const newCustomIcon = {
				id: `custom-${Date.now()}`,
				name: file.name.replace(/\.(svg|png)$/i, ''),
				dataUrl: e.target.result,
				type: file.type,
			};

			setCustomIcons((prev) => [...prev, newCustomIcon]);
			message.success('Custom icon added successfully!');
		};

		reader.onerror = () => {
			message.error('Failed to read file');
		};

		reader.readAsDataURL(file);

		event.target.value = '';
	};

	const handleAddCustomClick = () => {
		fileInputRef.current?.click();
	};

	return (
		<Modal
			title="Select Icon"
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={600}
			className="icon-selector-modal"
		>
			<Input
				placeholder="Search icons..."
				prefix={<SearchOutlined />}
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				className="icon-search-input"
			/>

			<div className="icon-grid">
				<Row gutter={[16, 16]}>
					<Col span={4}>
						<div
							className="icon-option add-custom-icon"
							onClick={handleAddCustomClick}
							title="Add Custom Icon"
						>
							<PlusOutlined style={{ fontSize: 30, color: COLORS.darkBlue }} />
							<span className="icon-name">Add Custom</span>
						</div>
					</Col>

					{filteredCustomIcons.map((customIcon) => (
						<Col key={customIcon.id} span={4}>
							<div
								className="icon-option custom-icon-option"
								onClick={() => handleSelectCustomIcon(customIcon)}
								title={customIcon.name}
							>
								<img
									src={customIcon.dataUrl}
									alt={customIcon.name}
									className="custom-icon-image"
								/>
								<span className="icon-name">{customIcon.name}</span>
							</div>
						</Col>
					))}

					{filteredIcons.map((iconName) => (
						<Col key={iconName} span={4}>
							<div
								className="icon-option"
								onClick={() => handleSelect(iconName)}
								title={iconName}
							>
								<WebIcon name={iconName} size={30} color={COLORS.darkBlue} />
								<span className="icon-name">{iconName}</span>
							</div>
						</Col>
					))}
				</Row>
			</div>

			{filteredIcons.length === 0 && filteredCustomIcons.length === 0 && (
				<div className="no-icons-found">
					No icons found matching "{searchTerm}"
				</div>
			)}

			<input
				ref={fileInputRef}
				type="file"
				accept=".svg,.png,image/svg+xml,image/png"
				onChange={handleFileUpload}
				style={{ display: 'none' }}
			/>
		</Modal>
	);
};
